/**
 * Defensible zone-consistency percentages ("share within tolerance").
 *
 * Every % has a one-sentence defense rendered next to it: point metrics =
 * share of laps within ±tolerance of the MEDIAN (a traffic lap can't pull the
 * reference, and counting caps its cost at its own 1-of-N share); the curve
 * metric = GRADED per bin (spec 2026-08-20) — the mean, across the zone's
 * 5 m bins, of the share of laps within ±tolerance of the bin median, so one
 * lap out of tolerance costs only its 1-of-N share of that bin rather than
 * failing the whole bin for every lap (computed elsewhere, in curvePercent.ts,
 * from the same series as the chart band). Outliers are counted AND named,
 * never auto-excluded.
 */
import {
  type TrackZone,
  type TrackZoneType,
  type ZoneLapData,
} from '../../../zones/TrackZoneTypes'
import { median, robustSpread, detectSingleOutlier } from './robustStats'
import { fastestZoneLapId } from '../lapExtremes'
import { speedToleranceKmh, distanceToleranceM } from './consistencyThresholds'
import {
  MIN_LAPS_FOR_CONSISTENCY,
  TIME_OK_THRESHOLD_MS,
  TIME_TOLERANCE_MS_PER_METER,
  TIME_TOLERANCE_STEP_MS,
  CONSISTENT_ROW_MIN_PCT,
  MIN_CURVE_BINS,
} from '../../../config/consistencyScoring'
import {
  type RatingBand,
  ROW_RATING_TIERS,
  ZONE_RATING_TIERS,
  ratingTierFor,
} from '../../../config/ratingBands'

export type PercentMetricKey =
  | 'brakePoint'
  | 'brakeRelease'
  | 'entrySpeed'
  | 'minSpeed'
  | 'minSpeedLocation'
  | 'exitSpeed'
  | 'throttlePoint'
  | 'speedCurve'
  | 'time'
export type PercentBand = RatingBand
export type PercentUnit = 'kmh' | 'm' | 'ms' | 'zone'
/** solid = raw ≥ 80 · one-off = raw < 80 but ≥ 80 excluding a named outlier · habit = < 80 either way */
export type PercentRowNature = 'solid' | 'one-off' | 'habit'

export interface PercentOutlier {
  lapNumber: number
  lapId: string
  delta: number
  pctExcluding: number
}
export interface PercentOffLap {
  lapNumber: number
  lapId: string
  /** Signed distance from the row median (value − median). */
  delta: number
}
export interface PercentRowStats {
  median: number
  typicalSpread: number
  outlierValue: number | null
}
export interface ConsistencyPercentRow {
  metric: PercentMetricKey
  label: string
  unit: PercentUnit
  pct: number
  band: PercentBand
  /** Attention-driving % — pctExcluding when a single outlier is named, else pct. */
  actionPct: number
  nature: PercentRowNature
  defense: string
  lapCount: number
  coverageNote: string | null
  outlier: PercentOutlier | null
  stats: PercentRowStats | null
  isLowest: boolean
  /** Data-bearing laps outside median ± tolerance on this row, with signed
   *  delta vs the median. Optional: absent on curve rows (bins, not laps). */
  offLaps?: PercentOffLap[]
}
export interface PercentMarker {
  metric: PercentMetricKey
  absoluteDistance: number
}
export interface ZoneOutlierAttribution {
  /** Ascending, distinct CULPRIT laps (fastest zone lap excluded) — the clients' red/dim set. */
  lapNumbers: number[]
  /** Ascending, distinct CULPRIT laps (fastest zone lap excluded) — the clients' red/dim set. Same order as lapNumbers. */
  lapIds: string[]
  text: string
}
export interface ZoneConsistencyPercent {
  status: 'ok' | 'insufficient-laps' | 'not-applicable' | 'traffic-disrupted'
  rows: ConsistencyPercentRow[]
  zonePct: number | null
  band: PercentBand | null
  isConsistent: boolean
  lowest: ConsistencyPercentRow | null
  marker: PercentMarker | null
  message: string | null
  attribution: ZoneOutlierAttribution | null
  /** Laps set aside as likely traffic (spec 2026-08-20) — ALWAYS rendered
   *  next to any % computed without them. Null when nothing was set aside. */
  setAside: ZoneOutlierAttribution | null
}

export function pctBand(pct: number): PercentBand {
  return ratingTierFor(pct, ROW_RATING_TIERS).band
}

/** Band for zone headlines and the session badge — green from 90, not 80.
 * Row-level percentages keep pctBand. */
export function zonePctBand(pct: number): PercentBand {
  return ratingTierFor(pct, ZONE_RATING_TIERS).band
}

/** Per-zone time tolerance: 2 ms per meter of zone length, rounded to 50 ms
 *  steps, floored at TIME_OK_THRESHOLD_MS. Longer zones accumulate
 *  proportionally more natural lap-time spread (owner calibration:
 *  100 m → 0.2s, 200 m → 0.4s). */
export function timeToleranceMs(zoneLengthMeters: number): number {
  const proportional = zoneLengthMeters * TIME_TOLERANCE_MS_PER_METER
  return Math.max(
    TIME_OK_THRESHOLD_MS,
    Math.round(proportional / TIME_TOLERANCE_STEP_MS) * TIME_TOLERANCE_STEP_MS
  )
}

export function rowNature(pct: number, actionPct: number): PercentRowNature {
  if (actionPct < CONSISTENT_ROW_MIN_PCT) return 'habit'
  return pct < CONSISTENT_ROW_MIN_PCT ? 'one-off' : 'solid'
}

interface DisplayBounds {
  startDistance: number
  endDistance: number
}

interface ToleranceCtx {
  values: number[]
  lapData: ZoneLapData[]
}

const speedRowTolerance = ({ values }: ToleranceCtx): number =>
  speedToleranceKmh(median(values))

const distanceRowTolerance =
  (refSpeed: (l: ZoneLapData) => number) =>
  ({ lapData }: ToleranceCtx): number =>
    distanceToleranceM(median(lapData.map(refSpeed)))

interface PointMetricDef {
  metric: PercentMetricKey
  label: string
  unit: PercentUnit
  tolerance: (ctx: ToleranceCtx) => number
  value: (l: ZoneLapData) => number | null
  markerAt: (ctx: {
    zone: TrackZone
    displayBounds: DisplayBounds
    values: number[]
    lapData: ZoneLapData[]
  }) => number
  coverageNoun: string
}

// Pass 26: exitSpeed markerAt moves to the ZONE end (was the padded
// display-window end — only braking zones differ, whose window extends
// into the next corner). entrySpeed markerAt is UNCHANGED:
// displayBounds.startDistance IS the entry gate under the owner's turn-in
// ruling, so value and marker already agree. cornering.minSpeed's markerAt
// and minSpeedLocation's value both read `apexRefDistance ?? apexDistance`
// — the native recalibrated argmin when available (never null, so `??` is
// the correct fallback operator), the raw odometer apex otherwise; the two
// always read the SAME expression so the row value and its map/chart
// marker can never disagree about which frame they live in. Brake point /
// brake release / throttle point value + markerAt stay on the raw
// odometer-aligned frame (spec §4.2 item 4 — position translation was
// dropped); per-zone atomicity upstream (computeRecalibratedZoneMetrics)
// still guarantees no row ever mixes frames across laps.
const POINT_METRICS: Record<TrackZoneType, PointMetricDef[]> = {
  braking: [
    {
      metric: 'brakePoint',
      label: 'Brake point',
      unit: 'm',
      tolerance: distanceRowTolerance((l) => l.entrySpeed),
      value: (l) => l.brakingPointDistance,
      coverageNoun: 'brake point',
      markerAt: ({ zone, values }) => zone.startDistance + median(values),
    },
    {
      metric: 'brakeRelease',
      label: 'Brake release',
      unit: 'm',
      tolerance: distanceRowTolerance((l) => l.minSpeed),
      value: (l) => l.brakeReleaseDistance,
      coverageNoun: 'brake release',
      markerAt: ({ zone, values }) => zone.startDistance + median(values),
    },
    {
      metric: 'entrySpeed',
      label: 'Entry speed',
      unit: 'kmh',
      tolerance: speedRowTolerance,
      value: (l) => l.entrySpeed,
      coverageNoun: 'entry speed',
      markerAt: ({ displayBounds }) => displayBounds.startDistance,
    },
    {
      metric: 'exitSpeed',
      label: 'Exit speed',
      unit: 'kmh',
      tolerance: speedRowTolerance,
      value: (l) => l.exitSpeed,
      coverageNoun: 'exit speed',
      markerAt: ({ zone }) => zone.endDistance,
    },
  ],
  cornering: [
    {
      metric: 'minSpeed',
      label: 'Min speed',
      unit: 'kmh',
      tolerance: speedRowTolerance,
      value: (l) => l.minSpeed,
      coverageNoun: 'min speed',
      markerAt: ({ zone, lapData }) =>
        zone.startDistance +
        median(lapData.map((l) => l.apexRefDistance ?? l.apexDistance)),
    },
    {
      metric: 'minSpeedLocation',
      label: 'Slowest point',
      unit: 'm',
      tolerance: distanceRowTolerance((l) => l.minSpeed),
      value: (l) => l.apexRefDistance ?? l.apexDistance,
      coverageNoun: 'slowest point',
      markerAt: ({ zone, values }) => zone.startDistance + median(values),
    },
    {
      metric: 'entrySpeed',
      label: 'Entry speed',
      unit: 'kmh',
      tolerance: speedRowTolerance,
      value: (l) => l.entrySpeed,
      coverageNoun: 'entry speed',
      markerAt: ({ displayBounds }) => displayBounds.startDistance,
    },
    {
      metric: 'exitSpeed',
      label: 'Exit speed',
      unit: 'kmh',
      tolerance: speedRowTolerance,
      value: (l) => l.exitSpeed,
      coverageNoun: 'exit speed',
      markerAt: ({ zone }) => zone.endDistance,
    },
  ],
  acceleration: [
    {
      metric: 'throttlePoint',
      label: 'Throttle point',
      unit: 'm',
      tolerance: distanceRowTolerance((l) => l.entrySpeed),
      value: (l) => l.throttlePointDistance,
      coverageNoun: 'throttle point',
      markerAt: ({ zone, values }) => zone.startDistance + median(values),
    },
    {
      metric: 'exitSpeed',
      label: 'Exit speed',
      unit: 'kmh',
      tolerance: speedRowTolerance,
      value: (l) => l.exitSpeed,
      coverageNoun: 'exit speed',
      markerAt: ({ zone }) => zone.endDistance,
    },
  ],
  straight: [],
}

function toleranceLabel(unit: PercentUnit, tolerance: number): string {
  if (unit === 'kmh') return `±${tolerance} km/h`
  // Total-window form (owner request, pass 26): ±5m reads as "within 10 m".
  // The math is unchanged — |v − median| ≤ tolerance; the window is 2×tol.
  if (unit === 'm') return `${tolerance * 2} m`
  return `${(tolerance / 1000).toFixed(2).replace(/\.?0+$/, '')}s`
}
interface Paired {
  value: number
  lapNumber: number
  lapId: string
}

function percentPointRow(
  def: PointMetricDef,
  lapData: ZoneLapData[]
): ConsistencyPercentRow | null {
  const paired: Paired[] = lapData
    .map((l) => ({
      value: def.value(l),
      lapNumber: l.lapNumber,
      lapId: l.lapId,
    }))
    .filter((p): p is Paired => p.value !== null)
  if (paired.length < MIN_LAPS_FOR_CONSISTENCY) return null
  const values = paired.map((p) => p.value)
  const med = median(values)
  const tol = def.tolerance({ values, lapData })
  const within = values.filter((v) => Math.abs(v - med) <= tol).length
  const pct = Math.round((100 * within) / values.length)

  const offLaps: PercentOffLap[] = paired
    .filter((p) => Math.abs(p.value - med) > tol)
    .map((p) => ({
      lapNumber: p.lapNumber,
      lapId: p.lapId,
      delta: p.value - med,
    }))

  const detected = detectSingleOutlier(values, tol)
  let outlier: PercentOutlier | null = null
  let withinExcl = 0
  if (detected) {
    const rest = values.filter((_, i) => i !== detected.index)
    const restMed = median(rest)
    withinExcl = rest.filter((v) => Math.abs(v - restMed) <= tol).length
    outlier = {
      lapNumber: paired[detected.index].lapNumber,
      lapId: paired[detected.index].lapId,
      delta: detected.delta,
      pctExcluding: Math.round((100 * withinExcl) / rest.length),
    }
  }

  const tolLabel = toleranceLabel(def.unit, tol)
  const timeSuffix = def.metric === 'time' ? ' of the median zone time' : ''
  // BINDING COPY — the single honest defense form: raw within-count over ALL
  // laps. The outlier set-aside never inflates a displayed number; it only
  // feeds attribution and the one-off/habit nature.
  const defense = `${within} of ${values.length} within ${tolLabel}${timeSuffix}`

  const actionPct = outlier ? outlier.pctExcluding : pct

  return {
    metric: def.metric,
    label: def.label,
    unit: def.unit,
    pct,
    band: pctBand(pct),
    actionPct,
    nature: rowNature(pct, actionPct),
    defense,
    lapCount: values.length,
    coverageNote:
      values.length < lapData.length
        ? `${values.length} of ${lapData.length} laps have ${def.coverageNoun} data`
        : null,
    outlier,
    stats: {
      median: med,
      typicalSpread: detected?.typicalSpread ?? robustSpread(values),
      outlierValue: detected?.value ?? null,
    },
    isLowest: false,
    offLaps,
  }
}

const timeMetricDef = (toleranceMs: number): PointMetricDef => ({
  metric: 'time',
  label: 'Time',
  unit: 'ms',
  tolerance: () => toleranceMs,
  value: (l) => l.zoneTimeMs,
  coverageNoun: 'time',
  markerAt: ({ displayBounds }) => displayBounds.endDistance,
})

export interface BuildPercentRowsArgs {
  zone: TrackZone
  lapData: ZoneLapData[]
  displayBounds: DisplayBounds
  /** Curve row, prebuilt by the caller from the band's spread series (Task 2). */
  curveRow?: ConsistencyPercentRow | null
}

export function buildPercentRows({
  zone,
  lapData,
  displayBounds,
  curveRow,
}: BuildPercentRowsArgs): ConsistencyPercentRow[] {
  void displayBounds
  const defs = POINT_METRICS[zone.type] ?? []
  const rows: ConsistencyPercentRow[] = []
  for (const def of defs) {
    const row = percentPointRow(def, lapData)
    if (row) rows.push(row)
  }
  if (curveRow) rows.push(curveRow)
  if (defs.length > 0) {
    const def = timeMetricDef(
      timeToleranceMs(zone.endDistance - zone.startDistance)
    )
    const timeRow = percentPointRow(def, lapData)
    if (timeRow) rows.push(timeRow)
  }
  return rows
}

export interface ComposeArgs {
  zone: TrackZone
  lapData: ZoneLapData[]
  displayBounds: DisplayBounds
  rows: ConsistencyPercentRow[]
  setAside?: ZoneOutlierAttribution | null
  /** Comparison-mode "Lap N (S2)" qualifiers, keyed by lapId (owner follow-up
   *  2026-08-21). Absent for the single-session path, so callers that never
   *  pass it get byte-identical attribution copy to before. */
  lapSessionLabels?: Map<string, string>
}

export function composeZoneConsistencyPercent({
  zone,
  lapData,
  displayBounds,
  rows,
  setAside,
  lapSessionLabels,
}: ComposeArgs): ZoneConsistencyPercent {
  const defs = POINT_METRICS[zone.type] ?? []
  if (defs.length === 0) {
    return {
      status: 'not-applicable',
      rows: [],
      zonePct: null,
      band: null,
      isConsistent: false,
      lowest: null,
      marker: null,
      message: null,
      attribution: null,
      setAside: null,
    }
  }
  if (lapData.length < MIN_LAPS_FOR_CONSISTENCY || rows.length === 0) {
    return {
      status: 'insufficient-laps',
      rows: [],
      zonePct: null,
      band: null,
      isConsistent: false,
      lowest: null,
      marker: null,
      message: 'Need 3+ laps to gauge consistency.',
      attribution: null,
      setAside: null,
    }
  }
  // Honest model (pass 14): every displayed aggregate runs on the raw pct.
  // The outlier machinery still names culprits (attribution) and separates
  // one-offs from habits, but never moves a number the racer sees.
  const lowest = rows.reduce(
    (a: ConsistencyPercentRow, b: ConsistencyPercentRow) =>
      b.pct < a.pct ? b : a
  )
  lowest.isLowest = true
  // Headline = the plain average of the rows below — every metric
  // contributes equally and the number explains itself. The band reads that
  // average on the zone-level scale (green from ZONE_PCT_GREEN_MIN, not the
  // row floor); focus/solid membership and severity are decided elsewhere.
  const zonePct = Math.round(
    rows.reduce((sum, r) => sum + r.pct, 0) / rows.length
  )
  const isConsistent = rows.every(
    (r: ConsistencyPercentRow) => r.pct >= CONSISTENT_ROW_MIN_PCT
  )
  const band = zonePctBand(zonePct)

  // The marker points at the row with the largest honest inconsistency —
  // since pass 14 that can be a one-off (the message/attribution copy
  // explains it); time/speedCurve rows have no single location to mark.
  let marker: PercentMarker | null = null
  const lowestDef = defs.find((d) => d.metric === lowest.metric)
  if (
    !isConsistent &&
    lowestDef &&
    lowest.metric !== 'time' &&
    lowest.metric !== 'speedCurve'
  ) {
    const values = lapData
      .map((l) => lowestDef.value(l))
      .filter((v): v is number => v !== null)
    marker = {
      metric: lowest.metric,
      absoluteDistance: lowestDef.markerAt({
        zone,
        displayBounds,
        values,
        lapData,
      }),
    }
  }

  const oneOffLapNumbers = [
    ...new Set(
      rows
        .filter((r) => r.nature === 'one-off' && r.outlier)
        .map((r) => r.outlier!.lapNumber)
    ),
  ].sort((a, b) => a - b)
  let message: string | null = null
  if (isConsistent) {
    message =
      oneOffLapNumbers.length > 0
        ? `Consistent except lap${oneOffLapNumbers.length > 1 ? 's' : ''} ${formatLapList(oneOffLapNumbers)} — one-off, not a pattern.`
        : 'All laps within tolerance — nothing to fix here.'
  }

  return {
    status: 'ok',
    rows,
    zonePct,
    band,
    isConsistent,
    lowest,
    marker,
    message,
    attribution: buildAttribution(
      rows,
      fastestZoneLapId(lapData),
      lapSessionLabels
    ),
    setAside: setAside ?? null,
  }
}

/** Pass 27 (C1): insight for a zone that is consistent yet held in focus by
 *  the pass-18 time-loss override — the story is pace, not spread. Composed
 *  once here; both clients render `percent.message` verbatim. */
export function focusConsistentMessage(timeLossMs: number): string {
  return `Consistent lines here — but ~${(timeLossMs / 1000).toFixed(1)}s slower than your best laps. The gap is pace, not consistency.`
}

/** Joins lap numbers (or pre-qualified "N (S2)" strings) for a sentence:
 *  "4", "2 and 4", "2, 3 and 4". */
function formatLapList<T extends string | number>(items: T[]): string {
  if (items.length === 1) return `${items[0]}`
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/** Comparison-mode lap-name qualifier (owner follow-up 2026-08-21): "N" for
 *  single-session copy, "N (S2)" when the lap's session has a label. The
 *  ONLY place attribution/set-aside text learns which session a lap came
 *  from — every caller that never passes `lapSessionLabels` gets back
 *  exactly `${lapNumber}`, so single-session copy stays byte-identical. */
export function qualifyLapNumber(
  lapNumber: number,
  lapId: string,
  lapSessionLabels?: Map<string, string>
): string {
  const label = lapSessionLabels?.get(lapId)
  return label ? `${lapNumber} (${label})` : `${lapNumber}`
}

/** Builds the clients' red/dim set and its one-sentence explanation.
 *  Culprit sources: (a) the named single outlier of any row — a one-off
 *  traffic lap stays marked — and (b) every off-cluster lap of rows below
 *  the action floor (the rows that make the zone inconsistent). Both
 *  sources pass through the time-cost gate: a lap is only ever red when it
 *  actually cost time — slower than the zone-time median by more than the
 *  zone-length tolerance (a slow-side off lap of the time row). Metric
 *  scatter on a time-quick lap stays visible in the rows but never reds
 *  the lap. The zone's fastest lap is never a culprit; when it surfaces in
 *  either source it gets the "stands out by being your fastest" clause
 *  instead — checked BEFORE the gate, since the fastest lap never costs
 *  time. */
function buildAttribution(
  rows: ConsistencyPercentRow[],
  fastestLapId: string | null,
  lapSessionLabels?: Map<string, string>
): ZoneOutlierAttribution | null {
  const timeRow = rows.find((r) => r.metric === 'time')
  const costsTime = new Set(
    (timeRow?.offLaps ?? [])
      .filter((off) => off.delta > 0)
      .map((off) => off.lapId)
  )
  const byLap = new Map<number, string>()
  let fastestOutlierLapNumber: number | null = null
  const consider = (lap: {
    lapNumber: number
    lapId: string
    delta: number
  }) => {
    if (lap.lapId === fastestLapId) {
      fastestOutlierLapNumber = lap.lapNumber
      return
    }
    if (!costsTime.has(lap.lapId)) return
    byLap.set(lap.lapNumber, lap.lapId)
  }
  for (const r of rows) {
    if (r.outlier) consider(r.outlier)
    if (r.actionPct < CONSISTENT_ROW_MIN_PCT) {
      for (const off of r.offLaps ?? []) consider(off)
    }
  }
  if (byLap.size === 0 && fastestOutlierLapNumber === null) return null
  const lapNumbers = [...byLap.keys()].sort((a, b) => a - b)
  const lapIds = lapNumbers.map((n) => byLap.get(n) as string)
  const qualifiedNumbers = lapNumbers.map((n, i) =>
    qualifyLapNumber(n, lapIds[i], lapSessionLabels)
  )
  let text: string
  if (lapNumbers.length === 0) {
    text = `Lap ${qualifyLapNumber(fastestOutlierLapNumber!, fastestLapId!, lapSessionLabels)} stands out here — by being your fastest. Stats below set it aside.`
  } else {
    text =
      lapNumbers.length === 1
        ? `Lap ${qualifiedNumbers[0]} was the odd one out here.`
        : `No single culprit — laps ${formatLapList(qualifiedNumbers)} each had an off moment.`
    if (fastestOutlierLapNumber !== null) {
      text += ` Lap ${qualifyLapNumber(fastestOutlierLapNumber, fastestLapId!, lapSessionLabels)} also stands out — by being your fastest.`
    }
  }
  return { lapNumbers, lapIds, text }
}

/** Chart-marker label for the lowest point row, e.g. "Brake pt ±8 m". */
export function formatPercentMarkerLabel(row: ConsistencyPercentRow): string {
  const short = row.label.replace(' speed', '').replace(' point', ' pt')
  const spread = Math.round(row.stats?.typicalSpread ?? 0)
  const unit = row.unit === 'kmh' ? 'km/h' : row.unit === 'm' ? 'm' : ''
  return `${short} ±${spread} ${unit}`.trim()
}
